import { z } from 'zod';

// Appointments and SMS reminders (docs/plans/phase2-appointments.md §3). Instants are UTC
// ISO strings; days are East Africa calendar days, `YYYY-MM-DD`. Internal UUIDs only in
// URLs and query strings.

export const APPOINTMENT_STATUSES = ['booked', 'arrived', 'cancelled', 'no_show'] as const;
export type AppointmentStatusCode = (typeof APPOINTMENT_STATUSES)[number];
export const APPOINTMENT_KINDS = ['new', 'follow_up'] as const;
export type AppointmentKind = (typeof APPOINTMENT_KINDS)[number];
export const SCHEDULE_STATUSES = ['active', 'inactive'] as const;
export const REMINDER_DECISIONS = ['granted', 'refused', 'withdrawn'] as const;
export type ReminderDecision = (typeof REMINDER_DECISIONS)[number];

const isoDaySchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Write the day as YYYY-MM-DD');
const timeSchema = z.string().regex(/^\d{2}:\d{2}$/, 'Write the time as HH:MM');
const reasonSchema = z.string().trim().min(3, 'Say why, in a few words').max(500);

export const weeklySessionSchema = z.object({
  /** Monday 1 to Sunday 7. */
  weekday: z.number().int().min(1).max(7),
  start: timeSchema,
  end: timeSchema,
});
export type WeeklySessionInput = z.infer<typeof weeklySessionSchema>;

export const scheduleBodySchema = z.object({
  name: z.string().trim().min(1, 'Name the schedule').max(80),
  /** The queue the patient joins at check-in. */
  servicePointId: z.uuid(),
  /** Null for a clinic-wide schedule. */
  practitionerUserId: z.uuid().nullable(),
  slotMinutes: z.number().int().min(5).max(240),
  slotCapacity: z.number().int().min(1).max(50),
  weeklySessions: z.array(weeklySessionSchema).min(1).max(28),
  validFrom: isoDaySchema,
  validUntil: isoDaySchema.nullable(),
  status: z.enum(SCHEDULE_STATUSES),
});
export type ScheduleBody = z.infer<typeof scheduleBodySchema>;

const namedRefSchema = z.object({ id: z.uuid(), name: z.string() });
const practitionerRefSchema = z.object({ userId: z.uuid(), name: z.string() });

export const scheduleViewSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  servicePoint: namedRefSchema,
  practitioner: practitionerRefSchema.nullable(),
  slotMinutes: z.number().int(),
  slotCapacity: z.number().int(),
  weeklySessions: z.array(weeklySessionSchema),
  validFrom: isoDaySchema,
  validUntil: isoDaySchema.nullable(),
  status: z.enum(SCHEDULE_STATUSES),
});
export type ScheduleView = z.infer<typeof scheduleViewSchema>;

export const scheduleListSchema = z.object({ items: z.array(scheduleViewSchema) });

/** Settings: every schedule, and what a schedule can point at. */
export const adminScheduleListSchema = z.object({
  items: z.array(scheduleViewSchema),
  clinics: z.array(namedRefSchema),
  practitioners: z.array(practitionerRefSchema),
});
export type AdminScheduleList = z.infer<typeof adminScheduleListSchema>;

export const slotsQuerySchema = z.object({ day: isoDaySchema });

export const slotViewSchema = z.object({
  startsAt: z.iso.datetime(),
  endsAt: z.iso.datetime(),
  capacity: z.number().int(),
  booked: z.number().int(),
  free: z.number().int(),
  /** The slot has started, so it can't be booked. */
  past: z.boolean(),
});
export type SlotView = z.infer<typeof slotViewSchema>;

export const slotListSchema = z.object({
  scheduleId: z.uuid(),
  day: isoDaySchema,
  slots: z.array(slotViewSchema),
});
export type SlotList = z.infer<typeof slotListSchema>;

/**
 * The SMS reminder as staff see it. `due`: waiting for its time; `sending`: handed to the
 * outbox (including retries); `sent`; `failed` (with the reason and what to do);
 * `not_sent` (with why none will go).
 */
export const REMINDER_STATES = ['due', 'sending', 'sent', 'failed', 'not_sent'] as const;
export type ReminderState = (typeof REMINDER_STATES)[number];

export const reminderViewSchema = z.object({
  state: z.enum(REMINDER_STATES),
  dueAt: z.iso.datetime().nullable(),
  /** A stable code for why it wasn't sent or failed. */
  code: z.string().nullable(),
  /** The words to show. */
  text: z.string(),
});
export type ReminderView = z.infer<typeof reminderViewSchema>;

export const appointmentViewSchema = z.object({
  id: z.uuid(),
  startsAt: z.iso.datetime(),
  endsAt: z.iso.datetime(),
  status: z.enum(APPOINTMENT_STATUSES),
  kind: z.enum(APPOINTMENT_KINDS),
  patient: z.object({ id: z.uuid(), displayName: z.string(), patientNumber: z.string() }),
  schedule: namedRefSchema,
  servicePoint: namedRefSchema,
  practitioner: practitionerRefSchema.nullable(),
  note: z.string().nullable(),
  statusReason: z.string().nullable(),
  sourceVisitId: z.uuid().nullable(),
  visitId: z.uuid().nullable(),
  rescheduleCount: z.number().int(),
  /** A booked appointment the schedule no longer has a slot for; reschedule it. */
  offSchedule: z.boolean(),
  reminder: reminderViewSchema.nullable(),
});
export type AppointmentView = z.infer<typeof appointmentViewSchema>;

export const appointmentDayQuerySchema = z.object({
  day: isoDaySchema,
  scheduleId: z.uuid().optional(),
});

export const appointmentDaySchema = z.object({
  day: isoDaySchema,
  items: z.array(appointmentViewSchema),
});
export type AppointmentDay = z.infer<typeof appointmentDaySchema>;

export const bookAppointmentBodySchema = z.object({
  /** Generated once per booking; a retried request returns the same appointment. */
  clientRequestId: z.uuid(),
  patientId: z.uuid(),
  scheduleId: z.uuid(),
  startsAt: z.iso.datetime(),
  kind: z.enum(APPOINTMENT_KINDS),
  /** The visit a follow-up was booked from. */
  sourceVisitId: z.uuid().optional(),
  /** For staff; never sent by SMS. */
  note: z.string().trim().max(200).optional(),
  /** The patient's answer about SMS reminders, when asked while booking. */
  smsReminders: z.enum(['granted', 'refused']).optional(),
});
export type BookAppointmentBody = z.infer<typeof bookAppointmentBodySchema>;

export const rescheduleAppointmentBodySchema = z.object({
  scheduleId: z.uuid(),
  startsAt: z.iso.datetime(),
  reason: reasonSchema,
});

export const cancelAppointmentBodySchema = z.object({ reason: reasonSchema });

export const noShowBodySchema = z.object({ reason: reasonSchema.optional() });

export const reminderConsentBodySchema = z
  .object({
    decision: z.enum(REMINDER_DECISIONS),
    reason: reasonSchema.optional(),
  })
  .refine((v) => v.decision !== 'withdrawn' || v.reason !== undefined, {
    message: 'Say why the patient no longer wants SMS reminders',
    path: ['reason'],
  });

export const reminderConsentViewSchema = z.object({
  decision: z.enum(REMINDER_DECISIONS).nullable(),
  capturedAt: z.iso.datetime().nullable(),
});
export type ReminderConsentView = z.infer<typeof reminderConsentViewSchema>;

export const patientAppointmentsSchema = z.object({
  /** Upcoming first (soonest first), then the last 90 days (latest first). */
  items: z.array(appointmentViewSchema),
  reminderConsent: reminderConsentViewSchema,
  /** The last four digits of the primary mobile, or null when none is on file. */
  mobileLastDigits: z.string().nullable(),
});
export type PatientAppointments = z.infer<typeof patientAppointmentsSchema>;
